/**
 * <ParentHomeSkeleton> — la forme de l'accueil parent pendant son premier chargement.
 *
 * Les blocs tombent là où tombera le contenu, section par section, avec les vraies surfaces : le
 * cadre des temps forts autour de la prochaine séance, les cartes, les cartes-listes et leurs
 * filets. Les intitulés de section sont les vrais (ils ne dépendent d'aucune donnée) ; seul ce qui
 * vient du serveur attend sous un bloc.
 *
 * Ce que le squelette ne devine pas : « À faire » (il n'est posé que si l'enfant a quelque chose
 * à faire), les chips d'état de la prochaine séance (« Aujourd'hui », « Rattrapage »…) et le
 * nombre de lignes de chaque liste — il montre le cas courant : deux séances « À venir », un
 * programme de révision et le prochain contrôle.
 *
 * Les tailles viennent des composants eux-mêmes, pour qu'une retouche s'y voie sans recopie : la
 * grille et la tuile des gestes (`QuickActionGrid`, `TILE_MIN_HEIGHT`), et les squelettes posés à
 * côté de leur composant (`<NextSessionHeroSkeleton>`, que l'accueil élève pose aussi,
 * `<LiveReportCardSkeleton>`, `<SessionRowSkeleton>`, `<SchoolAssessmentRowSkeleton>`). Restent
 * recopiées ici : la ligne du programme de révision, et la rangée « Dernier bilan » / « À venir » de
 * `<ParentHomePage>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { BUBBLE_SIZE } from "../../atoms/icons/IconBubble/IconBubble"
import { Card } from "../../molecules/Card"
import { CardList, CardListRow } from "../../molecules/CardList"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { List } from "../../molecules/List"
import { QuickActionGrid } from "../../molecules/QuickActionRow/QuickActionRow"
import { TILE_MIN_HEIGHT } from "../../molecules/QuickActionTile/QuickActionTile"
import { SchoolAssessmentRowSkeleton } from "../../molecules/SchoolAssessmentRow/SchoolAssessmentRowSkeleton"
import { SessionRowSkeleton } from "../../molecules/SessionRow/SessionRowSkeleton"
import { LiveReportCardSkeleton } from "../../organisms/LiveReportCard/LiveReportCardSkeleton"
import { NextSessionHeroSkeleton } from "../../organisms/NextSessionHero"

/** Les quatre gestes : la grille de `<QuickActionRow>`, et dans chaque tuile sa bulle et son libellé. */
function QuickActionBones() {
    return (
        <QuickActionGrid>
            {[0, 1, 2, 3].map((index) => (
                <Card key={index} p="4" style={styles.tile}>
                    <Row gap="3" align="center">
                        <Skeleton shape="circle" size={BUBBLE_SIZE} />
                        <Skeleton textSize="text-md" width="60%" />
                    </Row>
                </Card>
            ))}
        </QuickActionGrid>
    )
}

/** Une ligne qui porte un geste (« Voir plus », « Ajouter un contrôle ») : un mot. */
function LinkRowBones(props: { width: number }) {
    return (
        <CardListRow>
            <Skeleton textSize="text-md" width={props.width} />
        </CardListRow>
    )
}

/** Le programme de révision du suivi : le picto de matière, le titre et sa ligne de détail. */
function RevisionProgramRowBones() {
    return (
        <CardListRow>
            <Row gap="3" align="center">
                <Skeleton shape="circle" size={28} />
                <Stack gap="1" style={styles.fill}>
                    <Skeleton textSize="text-md" width={180} />
                    <Skeleton textSize="text-sm" width={110} />
                </Stack>
            </Row>
        </CardListRow>
    )
}

/** Le prochain contrôle : « Prochain contrôle », son titre, puis la date et sa chip J-n. */
export function ParentHomeSkeleton() {
    return (
        <Stack gap="6">
            <Row gap="3" align="center">
                <Skeleton shape="circle" size={48} />
                <Skeleton textSize="text-3xl" width={140} />
            </Row>

            <List title="Prochaine séance">
                <HighlightFrame>
                    <CardList>
                        <NextSessionHeroSkeleton />
                    </CardList>
                </HighlightFrame>
            </List>

            <QuickActionBones />

            <View style={styles.pairGrid}>
                <View style={styles.pairColumn}>
                    <List title="Dernier bilan">
                        <LiveReportCardSkeleton size="sm" commentLines={3} />
                    </List>
                </View>
                <View style={styles.pairColumn}>
                    <List title="À venir">
                        <CardList>
                            <SessionRowSkeleton />
                            <SessionRowSkeleton />
                            <LinkRowBones width={170} />
                        </CardList>
                    </List>
                </View>
            </View>

            <List title="Suivi">
                <CardList>
                    <RevisionProgramRowBones />
                    <SchoolAssessmentRowSkeleton />
                    <LinkRowBones width={150} />
                </CardList>
            </List>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    fill: { flex: 1, flexShrink: 1 },
    /** La tuile de `<QuickActionTile>` : sa hauteur, son contenu centré. */
    tile: {
        minHeight: TILE_MIN_HEIGHT,
        justifyContent: "center",
    },
    /** La rangée « Dernier bilan » / « À venir » de `<ParentHomePage>`. */
    pairGrid: {
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "flex-start" },
        gap: theme.spacing["6"],
    },
    pairColumn: {
        flexGrow: { xs: 0, md: 1 },
        flexShrink: 1,
        flexBasis: { xs: "auto", md: 0 },
    },
}))
